import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatCount, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { useGridSelection, selectedText } from '../../../../shared/table-builder/grid-selection'
import { Menu, MenuDetails, MenuItem } from '../../../../shared/menu/menu'
import { IconCheck, IconError, IconHide, IconRedo } from '../../../../shared/icon/icon'
import { useIsMobile } from '../../../../shared/hooks/use-is-mobile'
import { listGroups, getFacets, bulkAction } from './errors-api'
import { useErrorRoles, READ_ONLY } from './use-error-roles'
import { ErrorsCards } from './errors-cards'
import { STATUS_LABEL, STATUS_TONE, groupTitle, where, fromLine } from './errors-format'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import './errors.scss'
import { SEARCH_FIELD_PROPS } from '../../../../shared/component/search-field-props'

const WITHIN = [{ value: '', label: 'Any time' }, { value: '1h', label: 'Last hour', ms: 3600e3 }, { value: '24h', label: 'Last 24 hours', ms: 864e5 },
  { value: '7d', label: 'Last 7 days', ms: 6048e5 }, { value: '30d', label: 'Last 30 days', ms: 2592e6 }]

// The orders content's GET /errors/groups takes (each newest or most first).
export const SORTS = [
  { value: 'last_seen', label: 'Last seen' },
  { value: 'first_seen', label: 'First seen' },
  { value: 'events', label: 'Most events' },
  { value: 'last24h', label: 'Most in the last 24 hours', short: 'Most in 24 hours' },
  { value: 'people', label: 'Most people' }
]

// The most errors ticked one by one (content takes 200 ids at once).
export const MAX_SELECTED = 200

export const filterParams = f => {
  const w = WITHIN.find(x => x.value === f.within)
  return { status: f.status, service: f.service || undefined, source: f.source || undefined, app: f.app || undefined, platform: f.platform || undefined,
    q: f.q || undefined, since: w && w.ms ? new Date(Date.now() - w.ms).toISOString() : undefined, sort: f.sort || 'last_seen' }
}

// A list row's top source (content's topFrom: where most of the error's
// newest samples came from) and how many samples and sources that is.
export const topFromSub = t => {
  if (!t) return ''
  const more = t.lines > 1 ? ` · ${formatCount(t.lines - 1)} more` : ''
  return `${formatCount(t.count)} of ${plural(t.samples, 'sample')}${more}`
}
const TopFrom = ({ row }) => {
  const t = row && row.topFrom
  if (!t) return <span className="sw-hint">No samples kept</span>
  return (
    <span>
      <span className="er-from-top" title={`${fromLine(t)}: ${topFromSub(t)} (the newest kept, up to 100)`}>{t.display}</span>
      <span className="sw-cell-sub">{topFromSub(t)}</span>
    </span>
  )
}

const labelOf = row => `error ${row.id}`

// The Errors list: server errors, failed jobs and boot failures grouped by
// fingerprint. Grid on wider screens, cards on phones; triage needs
// SYS_ERRORS update and a session that isn't impersonating.
export const ErrorsPage = () => {
  const { canTriage, readOnly } = useErrorRoles()
  const mobile = useIsMobile()
  const history = useHistory()
  const [filters, setFilters] = useState({ status: 'open', service: '', source: '', app: '', platform: '', within: '', q: '', sort: 'last_seen' })
  const [facets, setFacets] = useState({ services: [], sources: [], apps: [], platforms: [] })
  const [error, failure, setError] = useErrorLine()
  const [notice, setNotice] = useState(null)
  // how many errors the filters match (content's count), for the header box
  const [total, setTotal] = useState(null)
  // the search the rows were read with, for "all matching"
  const searchRef = useRef('')
  const [gridKey, setGridKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const tableRef = useRef()
  const firstFilters = useRef(true)

  useEffect(() => {
    let live = true
    getFacets().then(f => live && setFacets({ services: (f && f.services) || [], sources: (f && f.sources) || [],
      apps: (f && f.apps) || [], platforms: (f && f.platforms) || [] }), () => {})
    return () => { live = false }
  }, [])
  // A filter change starts the list again; the first render already loads it.
  useEffect(() => {
    if (firstFilters.current) { firstFilters.current = false; return }
    setGridKey(k => k + 1)
  }, [filters])

  // The grid's own search box is the text search, as on the Audit and
  // Request logs; the phone cards have no grid, so they keep the field.
  const getRows = useMemo(() => async (start, end, search) => {
    try {
      setError(null)
      const q = mobile ? filters.q : search || ''
      searchRef.current = q
      const page = await listGroups({ ...filterParams({ ...filters, q }), offset: start, limit: Math.min(end - start, 200) })
      const count = (page && page.count) || 0
      setTotal(count)
      return { data: (page && page.data) || [], count }
    } catch (err) {
      setError(serverMessage(err, "Errors couldn't be loaded."), err)
      return { data: [], count: start }
    }
  }, [filters, mobile, setError])

  // The header box chooses every error the filters match ("all matching",
  // up to the 1,000 one action changes); a box ticks one, up to 200. A new
  // filter or search clears it.
  const selection = useGridSelection({ total, max: MAX_SELECTED, labelOf, resetKey: filters })
  // what select mode chose: ids, or the list's filters and search as its rows
  // were read, as of when "all matching" was chosen (upTo)
  const chosen = () => {
    const { sort, ...filter } = filterParams({ ...filters, q: searchRef.current })
    return selection.request({ ...filter, ...(selection.since ? { upTo: selection.since } : {}) })
  }

  const bulk = async action => {
    setBusy(true)
    setNotice(null)
    try {
      const r = await bulkAction(chosen(), action)
      setNotice(`${Number((r && r.changed) || 0).toLocaleString()} changed.`)
      selection.done()
      setGridKey(k => k + 1)
    } catch (err) {
      setError(serverMessage(err, "That didn't work. Try again."), err)
    } finally {
      setBusy(false)
    }
  }
  const selectActions = [
    <MenuItem key="resolve" Icon={IconCheck} text="Resolve selected" show={canTriage} disabled={selection.none || busy} onClick={() => bulk('resolve')} />,
    <MenuItem key="ignore" Icon={IconHide} text="Ignore selected" show={canTriage} disabled={selection.none || busy} onClick={() => bulk('ignore')} />,
    <MenuItem key="reopen" Icon={IconRedo} text="Reopen selected" show={canTriage} disabled={selection.none || busy} onClick={() => bulk('reopen')} />
  ]
  const set = k => e => {
    const value = e.target.value
    setFilters(f => ({ ...f, [k]: value }))
  }

  return (
    <FleetPage title="Errors" purpose="Server errors, failed jobs, boot failures and browser crashes, grouped so one bug is one row."
      actions={<Link className="btn sw-btn" to="/admin/alert-routing?tab=routes&category=errors">Error alert routes</Link>}>
      {readOnly && <Notice tone="warn" role="status">{READ_ONLY}</Notice>}
      <div className="sw-toolbar er-toolbar">
        {mobile && <>
          <label className="visually-hidden" htmlFor="er-q">Search</label>
          <input {...SEARCH_FIELD_PROPS} id="er-q" name="errors-search" className="form-control form-control-sm sw-search" placeholder="Search errors" title="Search the type, code or message (Enter to search)" defaultValue={filters.q}
            onKeyDown={e => e.key === 'Enter' && setFilters(f => ({ ...f, q: e.target.value.trim() }))} />
        </>}
        <select id="er-status" aria-label="Status" className="form-select form-select-sm" value={filters.status} onChange={set('status')}>
          <option value="open">Open</option><option value="resolved">Resolved</option><option value="ignored">Ignored</option><option value="all">Any status</option>
        </select>
        <select id="er-service" aria-label="Service" className="form-select form-select-sm" value={filters.service} onChange={set('service')}>
          <option value="">Every service</option>
          {facets.services.map(f => <option key={f.key} value={f.key}>{f.key} ({f.count})</option>)}
        </select>
        <select id="er-source" aria-label="Source" className="form-select form-select-sm" value={filters.source} onChange={set('source')}>
          <option value="">Any source</option><option value="request">Requests</option><option value="job">Jobs</option><option value="boot">Boot</option><option value="client">Browser</option>
        </select>
        <select id="er-app" aria-label="App" title="The app the error's newest samples came from" className="form-select form-select-sm" value={filters.app} onChange={set('app')}>
          <option value="">Any app</option>
          {facets.apps.map(f => <option key={f.key} value={f.key}>{f.key} ({f.count})</option>)}
        </select>
        <select id="er-platform" aria-label="Platform" title="The platform the error's newest samples came from" className="form-select form-select-sm" value={filters.platform} onChange={set('platform')}>
          <option value="">Any platform</option>
          {facets.platforms.map(f => <option key={f.key} value={f.key}>{f.key} ({f.count})</option>)}
        </select>
        <select id="er-within" aria-label="Last seen" className="form-select form-select-sm" value={filters.within} onChange={set('within')}>
          {WITHIN.map(w => <option key={w.value} value={w.value}>{w.value ? `Seen in the ${w.label.toLowerCase()}` : 'Seen any time'}</option>)}
        </select>
        <select id="er-sort" aria-label="Sort by" className="form-select form-select-sm" value={filters.sort} onChange={set('sort')}>
          {SORTS.map(o => <option key={o.value} value={o.value}>Sort: {(o.short || o.label).toLowerCase()}</option>)}
        </select>
      </div>
      {mobile && canTriage && !selection.none && (
        <div className="er-bulk" role="group" aria-label="Selected errors">
          <span className="er-bulk-count">{selectedText(selection)}{selection.atMax ? ' (the most at once)' : ''}</span>
          <button type="button" className="btn btn-sm sw-btn-primary" disabled={busy} onClick={() => bulk('resolve')}>Resolve selected</button>
          <button type="button" className="btn btn-sm sw-btn" disabled={busy} onClick={() => bulk('ignore')}>Ignore selected</button>
          <button type="button" className="btn btn-sm sw-btn" disabled={busy} onClick={() => bulk('reopen')}>Reopen selected</button>
        </div>
      )}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {notice && <Notice tone="ok">{notice}</Notice>}
      {mobile ? (
        <ErrorsCards key={gridKey} load={getRows} selection={canTriage ? selection : null} />
      ) : (
          <FleetGrid title="errors" subtitle={`Sorted by ${SORTS.find(o => o.value === filters.sort).label.toLowerCase()}`} tableRef={tableRef} exportName="errors" rowHeight={ROW_HEIGHT_TWO_LINE}
            selection={canTriage ? selection : null} selectActions={selectActions}>
            <TableAgInfinite pinFirstColumn key={gridKey} ref={tableRef} queryKey="" onGetRows={getRows} rowHeight={ROW_HEIGHT_TWO_LINE} selection={canTriage ? selection : null}
              onRowClicked={row => row && history.push(`/admin/errors/${row.id}`)}
              emptyState={{ Icon: IconError, title: 'No errors', message: 'Nothing matches these filters.' }}>
              <ColumnFormat field="status" header="Status" sortable={false} minWidth={110} flex={0.5}
                render={r => (r.data ? <Pill tone={STATUS_TONE[r.value]}>{STATUS_LABEL[r.value] || r.value}</Pill> : '')} />
              <ColumnFormat field="errorType" header="Error" sortable={false} minWidth={260} flex={2} stacked
                render={r => (r.data ? <span><span className="fw-semibold">{groupTitle(r.data)}</span><span className="sw-cell-sub sw-mono" title={r.data.locationLine || r.data.location}>{r.data.location}</span></span> : '')}
                exportValue={groupTitle} />
              <ColumnFormat field="service" header="Service" sortable={false} minWidth={110} flex={0.6} render={r => r.value || ''} />
              <ColumnFormat field="route" header="Where" sortable={false} minWidth={200} flex={1.4} render={r => (r.data ? <span className={r.data.source === 'request' ? 'sw-mono' : ''}>{where(r.data)}</span> : '')} exportValue={where} />
              <ColumnFormat field="topFrom" header="From" sortable={false} minWidth={190} flex={1.1} stacked render={r => (r.data ? <TopFrom row={r.data} /> : '')}
                exportValue={g => (g && g.topFrom ? g.topFrom.display : '')} />
              <ColumnFormat field="lastSeenAt" header="Last seen" sortable={false} minWidth={120} render={r => (r.data ? <span title={formatDateTime(r.value)}>{formatAgo(r.value)}</span> : '')} />
              <ColumnFormat field="firstSeenAt" header="First seen" sortable={false} minWidth={120} render={r => (r.data ? <span title={formatDateTime(r.value)}>{formatAgo(r.value)}</span> : '')} />
              <ColumnFormat field="totalCount" header="Events" sortable={false} minWidth={90} flex={0.5} render={r => (r.data ? Number(r.value || 0).toLocaleString() : '')} />
              <ColumnFormat field="last24h" header="Last 24h" sortable={false} minWidth={90} flex={0.5} render={r => (r.data ? Number(r.value || 0).toLocaleString() : '')} />
              <ColumnFormat field="peopleCount" header="People" sortable={false} minWidth={90} flex={0.5} render={r => (r.data ? r.value : '')} />
              <ColumnFormat field="linkCount" header="Links" sortable={false} minWidth={80} flex={0.4} render={r => (r.data && r.value ? r.value : '')} />
              <ColumnMenu menu={r => <Menu small><MenuDetails text="open" pin show onClick={() => history.push(`/admin/errors/${r.id}`)} /></Menu>} />
            </TableAgInfinite>
          </FleetGrid>
      )}
    </FleetPage>
  )
}
